import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listLeads, updateLead, createLead, STAGES } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("mls_pipe_view") || "stage");
  const [collapsedWeeks, setCollapsedWeeks] = useState({});
  const [form, setForm] = useState({
    name: "", role: "", company: "", industry: "", location: "",
    email: "", linkedin: "", website: "", fit_score: 60, fit_reason: "", notes: "",
  });

  const load = () => listLeads().then(setLeads).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const byStage = (sid) => leads.filter((l) => l.stage === sid);

  // Cluster by ISO-week of updated_at (falls back to created_at). Newest week first.
  const weekKey = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return "unknown";
    const day = d.getUTCDay() || 7; // 1..7 (Mon..Sun)
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() - (day - 1));
    return monday.toISOString().slice(0, 10);
  };
  const weekLabel = (key) => {
    if (key === "unknown") return "Unknown week";
    const start = new Date(key);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const fmt = (dd) => dd.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    return `Week of ${fmt(start)} — ${fmt(end)}`;
  };
  const weeksMap = leads.reduce((m, l) => {
    const k = weekKey(l.updated_at || l.created_at);
    (m[k] = m[k] || []).push(l);
    return m;
  }, {});
  const weekKeys = Object.keys(weeksMap).sort().reverse();

  const setView = (v) => {
    setViewMode(v);
    localStorage.setItem("mls_pipe_view", v);
  };
  const toggleWeek = (k) => setCollapsedWeeks((c) => ({ ...c, [k]: !c[k] }));

  const onSaveNew = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await createLead({
        ...form,
        fit_score: Number(form.fit_score) || 0,
        signals: [],
      });
      toast.success("Lead added");
      setShowAdd(false);
      setForm({ name: "", role: "", company: "", industry: "", location: "",
        email: "", linkedin: "", website: "", fit_score: 60, fit_reason: "", notes: "" });
      load();
    } catch {
      toast.error("Could not add lead");
    } finally {
      setSaving(false);
    }
  };

  const onDrop = async (stageId) => {
    if (!dragId) return;
    const target = leads.find((l) => l.id === dragId);
    setOverStage(null);
    setDragId(null);
    if (!target || target.stage === stageId) return;
    setLeads((arr) => arr.map((l) => (l.id === dragId ? { ...l, stage: stageId } : l)));
    try {
      await updateLead(dragId, { stage: stageId });
      toast.success(`Moved to ${stageId}`);
    } catch (e) {
      toast.error("Move failed");
      load();
    }
  };

  return (
    <div className="min-h-screen">
      <header className="px-12 py-10 border-b border-[#222] flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="uppercase-mono text-[#8a8a8a]">Section 03 / Pipeline</div>
          <h1 className="font-serif text-5xl lg:text-6xl mt-3" data-testid="pipeline-title">
            Move the conversation forward.
          </h1>
          <p className="text-[#8a8a8a] mt-4 max-w-2xl">Drag prospects across stages. Click any card to open the lead&apos;s outreach contact details.</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-lead-btn"
          className="btn-accent px-5 py-3 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
        >
          <Plus size={14} strokeWidth={1.5} /> Add lead manually
        </button>
      </header>

      {/* View mode toggle */}
      <div className="px-12 py-4 border-b border-[#222] flex items-center gap-3" data-testid="view-toggle">
        <span className="uppercase-mono text-[#8a8a8a]">View</span>
        {[
          { id: "stage", label: "By stage" },
          { id: "week", label: "By week of contact" },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => setView(m.id)}
            data-testid={`view-${m.id}`}
            className={`uppercase-mono px-3 py-1 border transition-colors ${
              viewMode === m.id
                ? "bg-[#CCFF00] text-black border-[#CCFF00]"
                : "border-[#222] text-[#8a8a8a] hover:border-[#CCFF00] hover:text-[#CCFF00]"
            }`}
          >
            {m.label}
          </button>
        ))}
        <span className="uppercase-mono text-[#555] ml-auto">{leads.length} total</span>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" data-testid="add-lead-modal">
          <div className="bg-[#0A0A0A] border border-[#CCFF00] w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#222]">
              <div className="font-serif text-2xl">Add lead manually</div>
              <button onClick={() => setShowAdd(false)} data-testid="close-add-lead" className="text-[#8a8a8a] hover:text-[#FF3333]">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["name", "Full name *", "Aditya Somaiah"],
                ["role", "Role", "Creative Director"],
                ["company", "Company", "Acme Studio"],
                ["industry", "Industry", "Museums"],
                ["location", "Location", "New York, NY"],
                ["email", "Email", "adi@acme.com"],
                ["linkedin", "LinkedIn URL", "https://linkedin.com/in/…"],
                ["website", "Website", "acme.com"],
                ["fit_score", "Fit score (0–100)", "60"],
              ].map(([k, label, placeholder]) => (
                <label key={k} className="block">
                  <div className="uppercase-mono text-[#8a8a8a] mb-1">{label}</div>
                  <input
                    type={k === "fit_score" ? "number" : "text"}
                    value={form[k]}
                    onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                    placeholder={placeholder}
                    data-testid={`add-lead-${k}`}
                    className="w-full px-3 py-2 text-sm"
                  />
                </label>
              ))}
              <label className="block md:col-span-2">
                <div className="uppercase-mono text-[#8a8a8a] mb-1">Why they fit</div>
                <textarea
                  value={form.fit_reason}
                  onChange={(e) => setForm((f) => ({ ...f, fit_reason: e.target.value }))}
                  rows={2}
                  data-testid="add-lead-fit-reason"
                  className="w-full px-3 py-2 text-sm"
                />
              </label>
              <label className="block md:col-span-2">
                <div className="uppercase-mono text-[#8a8a8a] mb-1">Notes</div>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  data-testid="add-lead-notes"
                  className="w-full px-3 py-2 text-sm"
                />
              </label>
            </div>
            <div className="px-6 py-4 border-t border-[#222] flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="btn-ghost px-4 py-2 uppercase tracking-widest text-[11px]">
                cancel
              </button>
              <button
                onClick={onSaveNew}
                disabled={saving}
                data-testid="save-add-lead"
                className="btn-accent px-5 py-2 uppercase tracking-widest text-[11px] disabled:opacity-50"
              >
                {saving ? "saving…" : "Save lead"}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === "week" && (
        <div className="px-12 py-8 space-y-6" data-testid="week-board">
          {weekKeys.length === 0 && (
            <div className="text-[11px] uppercase tracking-widest text-[#444] py-16 text-center border border-dashed border-[#222]">
              No leads yet
            </div>
          )}
          {weekKeys.map((wk) => {
            const items = weeksMap[wk];
            const collapsed = collapsedWeeks[wk];
            // group by stage inside the week, respecting STAGES order
            const groupedByStage = STAGES.map((s) => ({
              stage: s,
              items: items.filter((l) => l.stage === s.id),
            })).filter((g) => g.items.length > 0);
            return (
              <div key={wk} data-testid={`week-block-${wk}`} className="border border-[#222]">
                <button
                  onClick={() => toggleWeek(wk)}
                  data-testid={`week-toggle-${wk}`}
                  className="w-full flex items-baseline justify-between px-6 py-4 border-b border-[#222] hover:bg-[#0e0e0e] transition-colors text-left"
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-[11px] text-[#CCFF00]">{collapsed ? "+" : "–"}</span>
                    <span className="font-serif text-2xl">{weekLabel(wk)}</span>
                  </div>
                  <span className="uppercase-mono text-[#8a8a8a]">
                    {items.length} lead{items.length === 1 ? "" : "s"}
                  </span>
                </button>
                {!collapsed && (
                  <div className="p-4 space-y-4">
                    {groupedByStage.map(({ stage, items: stageItems }) => (
                      <div key={stage.id} data-testid={`week-${wk}-stage-${stage.id}`}>
                        <div className="flex items-baseline gap-3 mb-2 px-2">
                          <span className="uppercase-mono text-[#ededed]">{stage.label}</span>
                          <span className="font-mono text-[11px] text-[#8a8a8a]">{stageItems.length}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                          {stageItems.map((l) => (
                            <Link
                              key={l.id}
                              to={`/app/leads/${l.id}`}
                              data-testid={`week-card-${l.id}`}
                              className="block border border-[#222] p-4 hover:border-[#CCFF00] transition-colors bg-[#0A0A0A]"
                            >
                              <div className="flex items-baseline justify-between gap-2">
                                <div className="font-serif text-lg leading-tight truncate">{l.name}</div>
                                <div className="font-mono text-[11px] text-[#CCFF00]">{l.fit_score}</div>
                              </div>
                              <div className="uppercase-mono text-[#8a8a8a] mt-1 truncate">
                                {l.role || "—"}
                              </div>
                              <div className="uppercase-mono text-[#555] truncate">{l.company}</div>
                              {l.signals?.length > 0 && (
                                <div className="mt-3 text-[10px] text-[#8a8a8a] line-clamp-2">{l.signals[0]}</div>
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className={viewMode === "stage" ? "overflow-x-auto" : "hidden"} data-testid="kanban-board">
        <div className="flex min-w-max">
          {STAGES.map((s, idx) => {
            const items = byStage(s.id);
            const isOver = overStage === s.id;
            return (
              <div
                key={s.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverStage(s.id);
                }}
                onDragLeave={() => setOverStage((prev) => (prev === s.id ? null : prev))}
                onDrop={() => onDrop(s.id)}
                data-testid={`kanban-col-${s.id}`}
                className={`w-[300px] shrink-0 ${idx < STAGES.length - 1 ? "border-r border-[#222]" : ""} ${
                  isOver ? "bg-[#121212]" : ""
                } transition-colors`}
              >
                <div className="px-6 py-5 border-b border-[#222] flex items-baseline justify-between">
                  <div className="uppercase-mono text-[#ededed]">{s.label}</div>
                  <div className="font-mono text-[11px] text-[#8a8a8a]">{items.length}</div>
                </div>

                <div className="p-4 space-y-3 min-h-[400px]">
                  {items.length === 0 && (
                    <div className="text-[11px] uppercase tracking-widest text-[#444] py-12 text-center border border-dashed border-[#222]">
                      empty
                    </div>
                  )}
                  {items.map((l) => (
                    <Link
                      key={l.id}
                      to={`/app/leads/${l.id}`}
                      draggable
                      onDragStart={() => setDragId(l.id)}
                      onDragEnd={() => {
                        setDragId(null);
                        setOverStage(null);
                      }}
                      data-testid={`kanban-card-${l.id}`}
                      className={`block border border-[#222] p-4 hover:border-[#CCFF00] transition-colors bg-[#0A0A0A] ${
                        dragId === l.id ? "opacity-50 scale-95 border-[#CCFF00]" : ""
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-serif text-lg leading-tight truncate">{l.name}</div>
                        <div className="font-mono text-[11px] text-[#CCFF00]">{l.fit_score}</div>
                      </div>
                      <div className="uppercase-mono text-[#8a8a8a] mt-1 truncate">
                        {l.role || "—"}
                      </div>
                      <div className="uppercase-mono text-[#555] truncate">{l.company}</div>
                      {l.signals?.length > 0 && (
                        <div className="mt-3 text-[10px] text-[#8a8a8a] line-clamp-2">{l.signals[0]}</div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
